import React, { useState } from 'react';
import { 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Typography, 
  Snackbar,
  List,
  ListItem,
  ListItemText,
  Avatar,
  ListItemAvatar,
  Chip
} from '@mui/material';
import { Business, Add, CheckCircle, Email, Phone, Person } from '@mui/icons-material';
import styled from '@emotion/styled';

// Dummy organizations data
const initialOrganizations = [
  {
    id: 1,
    name: 'Acme Corp',
    email: 'contact@acme.com',
    phone: '+1 555-1234',
    address: 'New York, USA',
    admin: 'john@acme.com',
    status: 'active'
  },
  {
    id: 2,
    name: 'Globex Inc',
    email: 'info@globex.com',
    phone: '+44 20 7946 0958',
    address: 'London, UK',
    admin: 'sarah@globex.com',
    status: 'pending'
  }
];

const FormContainer = styled(Paper)`
  padding: 2rem;
  margin-bottom: 2rem;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
`;

const OrganizationList = styled(Paper)`
  padding: 1rem;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
`;

const CreateOrganization = () => {
  const [organizations, setOrganizations] = useState(initialOrganizations);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    adminName: '',
    adminEmail: ''
  });
  const [successOpen, setSuccessOpen] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newOrganization = {
      id: organizations.length + 1,
      ...formData,
      status: 'active'
    };
    setOrganizations([...organizations, newOrganization]);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      adminName: '',
      adminEmail: ''
    });
    setSuccessOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSuccessOpen(false);
  };

  return (
    <div style={{ padding: '2rem' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
        Organization Management
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <FormContainer>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <Add sx={{ mr: 1 }} /> Create New Organization
            </Typography>
            
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Organization Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                margin="normal"
                variant="outlined"
              />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Organization Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={3}
              />

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 500 }}>
                Admin Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Admin Name"
                    name="adminName"
                    value={formData.adminName}
                    onChange={handleChange}
                    required
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Admin Email"
                    name="adminEmail"
                    type="email"
                    value={formData.adminEmail}
                    onChange={handleChange}
                    required
                    margin="normal"
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                sx={{ mt: 3, px: 4, py: 1.5, borderRadius: '8px' }}
                startIcon={<CheckCircle />}
              >
                Create Organization
              </Button>
            </form>
          </FormContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <OrganizationList>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <Business sx={{ mr: 1 }} /> Existing Organizations ({organizations.length})
            </Typography>
            
            <List>
              {organizations.map((org) => (
                <ListItem key={org.id} sx={{ 
                  mb: 1, 
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  '&:hover': { backgroundColor: '#f8fafc' }
                }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#667eea20' }}>
                      <Business sx={{ color: '#667eea' }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={org.name}
                    secondary={
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                          <Chip
                            label={org.status}
                            size="small"
                            color={org.status === 'active' ? 'success' : 'warning'}
                            variant="outlined"
                          />
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Person fontSize="small" /> {org.admin}
                          </span>
                        </div>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </OrganizationList>
        </Grid>
      </Grid>

      <Snackbar
        open={successOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="Organization created successfully!"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            borderRadius: '8px',
            bgcolor: '#48bb78',
            color: '#ffffff',
            fontWeight: 500
          }
        }}
      />
    </div>
  );
};

export default CreateOrganization;